import { z } from "zod";
import i18n from "@core/i18n";

// Schema pour la création de sondage
export const PollCreateSchema = z.object({
  title: z
    .string()
    .min(1, i18n.t("validation.poll.titleRequired"))
    .max(200, i18n.t("validation.poll.titleMax")),

  description: z
    .string()
    .max(1000, i18n.t("validation.poll.descriptionMax"))
    .optional(),

  selectedDates: z
    .array(z.string().date())
    .min(1, i18n.t("validation.poll.datesMin"))
    .max(30, i18n.t("validation.poll.datesMax")),

  timeSlots: z
    .record(
      z.string().date(),
      z.array(
        z.object({
          hour: z.number().min(0).max(23),
          minute: z.number().min(0).max(59),
          duration: z.number().min(15).max(480), // 15min à 8h
        }),
      ),
    )
    .optional(),

  participantEmails: z
    .array(z.string().email(i18n.t("validation.common.emailInvalid")))
    .max(100, i18n.t("validation.poll.participantsMax")),

  settings: z
    .object({
      timeGranularity: z.number().default(60),
      allowAnonymousVotes: z.boolean().default(true),
      allowMaybeVotes: z.boolean().default(true),
      sendNotifications: z.boolean().default(true),
      expiresAt: z.string().datetime().optional(),
    })
    .optional(),
});

// Schema pour le vote
export const VoteCreateSchema = z.object({
  voterName: z
    .string()
    .min(1, i18n.t("validation.vote.nameRequired"))
    .max(100, i18n.t("validation.vote.nameMax")),

  voterEmail: z.string().email(i18n.t("validation.common.emailInvalid")),

  selections: z.record(
    z.string().uuid(), // option_id
    z.object({
      vote: z.enum(["yes", "no", "maybe"]),
      timeSlots: z
        .record(
          z.string(), // slot_id
          z.enum(["yes", "no", "maybe"]),
        )
        .optional(),
    }),
  ),

  comment: z.string().max(500, i18n.t("validation.vote.commentMax")).optional(),
});

// Schema pour la mise à jour du profil
export const ProfileUpdateSchema = z.object({
  fullName: z
    .string()
    .min(1, i18n.t("validation.profile.fullNameRequired"))
    .max(100, i18n.t("validation.profile.fullNameMax"))
    .optional(),

  timezone: z.string().default("Europe/Paris").optional(),

  preferences: z
    .object({
      language: z.enum(["fr", "en"]).default("fr"),
      notifications: z
        .object({
          emailInvitations: z.boolean().default(true),
          emailReminders: z.boolean().default(true),
          pushNotifications: z.boolean().default(false),
        })
        .optional(),
      ui: z
        .object({
          theme: z.enum(["light", "dark", "system"]).default("light"),
          defaultGranularity: z.number().default(60),
          defaultTimezone: z.string().default("Europe/Paris"),
        })
        .optional(),
    })
    .optional(),
});

// Schema pour l'inscription
export const SignUpSchema = z
  .object({
    email: z.string().email(i18n.t("validation.common.emailInvalid")),

    password: z
      .string()
      .min(8, i18n.t("validation.auth.passwordMin"))
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        i18n.t("validation.auth.passwordComplexity"),
      ),

    confirmPassword: z
      .string()
      .min(1, i18n.t("validation.auth.confirmPasswordRequired")),

    fullName: z
      .string()
      .min(1, i18n.t("validation.profile.fullNameRequired"))
      .max(100, i18n.t("validation.profile.fullNameMax")),

    termsAccepted: z.literal(true, {
      errorMap: () => ({
        message: i18n.t("validation.auth.termsRequired"),
      }),
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: i18n.t("validation.auth.passwordsMismatch"),
    path: ["confirmPassword"],
  });

// Schema pour la connexion
export const SignInSchema = z.object({
  email: z.string().email(i18n.t("validation.common.emailInvalid")),

  password: z.string().min(1, i18n.t("validation.auth.passwordRequired")),
});

// Schema pour la conversation IA
export const ConversationCreateSchema = z.object({
  sessionId: z
    .string()
    .uuid(i18n.t("validation.conversation.sessionIdInvalid"))
    .optional(),

  initialMessage: z
    .string()
    .min(1, i18n.t("validation.conversation.initialMessageRequired"))
    .max(1000, i18n.t("validation.conversation.messageMax")),
});

export const MessageCreateSchema = z.object({
  content: z
    .string()
    .min(1, i18n.t("validation.conversation.messageEmpty"))
    .max(1000, i18n.t("validation.conversation.messageMax")),
});

// Types dérivés des schémas
export type PollCreateInput = z.infer<typeof PollCreateSchema>;
export type VoteCreateInput = z.infer<typeof VoteCreateSchema>;
export type ProfileUpdateInput = z.infer<typeof ProfileUpdateSchema>;
export type SignUpInput = z.infer<typeof SignUpSchema>;
export type SignInInput = z.infer<typeof SignInSchema>;
export type ConversationCreateInput = z.infer<typeof ConversationCreateSchema>;
export type MessageCreateInput = z.infer<typeof MessageCreateSchema>;

// Helpers de validation
export function validatePollData(data: unknown): PollCreateInput {
  return PollCreateSchema.parse(data);
}

export function validateVoteData(data: unknown): VoteCreateInput {
  return VoteCreateSchema.parse(data);
}

export function validateProfileData(data: unknown): ProfileUpdateInput {
  return ProfileUpdateSchema.parse(data);
}

// Helper pour formater les erreurs Zod
export function formatZodError(error: z.ZodError): Record<string, string> {
  const formattedErrors: Record<string, string> = {};

  error.errors.forEach((err) => {
    const path = err.path.join(".");
    formattedErrors[path] = err.message;
  });

  return formattedErrors;
}
